const clampRatio = (value) => Math.max(0, Math.min(1, value))

const resolveHoldings = (params) => {
  if (Array.isArray(params?.holdings) && params.holdings.length) return params.holdings
  return [{
    symbol: params?.symbol ?? 'ONDS',
    shares: params?.shares ?? 0,
    avgCost: params?.avgCost ?? 0,
    maintenanceMargin: params?.maintenanceMargin ?? 0,
    primary: true,
    price: 0,
  }]
}

const priceFor = (holding, primaryPrice) => {
  if (holding.primary) return primaryPrice > 0 ? primaryPrice : (holding.price ?? 0)
  return holding.price ?? 0
}

export const computePortfolio = (params, quote) => {
  const primaryPrice = quote?.price ?? 0
  const holdings = resolveHoldings(params)
  const primary = holdings.find((h) => h.primary) ?? holdings[0]
  const taxRate = params?.taxRateOnGains ?? 0
  const marginDebt = params?.marginDebt ?? 0
  const personalLoan = params?.personalLoan ?? 0
  const netWorthGoal = params?.netWorthGoal ?? 0

  let costBasis = 0
  let marketValue = 0
  let taxLiability = 0
  let secondaryBuffer = 0

  const breakdown = holdings.map((h) => {
    const price = priceFor(h, primaryPrice)
    const basis = h.shares * h.avgCost
    const mv = h.shares * price
    const pnl = mv - basis
    costBasis += basis
    marketValue += mv
    if (pnl > 0) taxLiability += pnl * taxRate
    if (h !== primary) secondaryBuffer += mv * (1 - (h.maintenanceMargin ?? 0))
    return {
      symbol: h.symbol,
      shares: h.shares,
      avgCost: h.avgCost,
      price,
      marketValue: mv,
      costBasis: basis,
      unrealizedPnl: pnl,
      unrealizedPnlRatio: basis > 0 ? pnl / basis : 0,
      maintenanceMargin: h.maintenanceMargin ?? 0,
      primary: !!h.primary,
    }
  })

  const pnl = marketValue - costBasis
  const equity = marketValue - marginDebt
  const primaryDenom = primary ? primary.shares * (1 - (primary.maintenanceMargin ?? 0)) : 0
  const marginCallPrice = primaryDenom > 0 ? Math.max(0, (marginDebt - secondaryBuffer) / primaryDenom) : 0
  const marginHeadroom = primaryPrice > 0 ? (primaryPrice - marginCallPrice) / primaryPrice : 0
  const net = equity - taxLiability - personalLoan

  return {
    price: primaryPrice,
    shares: primary?.shares ?? 0,
    avgCost: primary?.avgCost ?? 0,
    holdings: breakdown,
    costBasis,
    marketValue,
    unrealizedPnl: pnl,
    unrealizedPnlRatio: costBasis > 0 ? pnl / costBasis : 0,
    taxLiability,
    positionEquity: equity,
    marginDebt,
    personalLoan,
    marginCallPrice,
    marginHeadroom,
    netWorth: net,
    netWorthGoal,
    goalProgress: clampRatio(net / (netWorthGoal || 1)),
    goalGap: netWorthGoal - net,
  }
}
